import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import Formulario from "../../Componentes/Utils/Formulario";
import FormComp from "../../Componentes/FormComp";
import useApi from "../../hooks/useApi";
import ModalAdicionarLicencasColaborador from "./ModalAdicionarLicencasColaborador";

const LICENCAS_ENDPOINT = "/Painel/API/InventarioTI/GetLicencasColaborador/";
const REMOVE_LICENCA_ENDPOINT = "/Painel/API/InventarioTI/RemoveColabLicenca/";
const UPDATE_CONTATO_ENDPOINT = "/Painel/API/InventarioTI/UpdateColabLicencaContato/";

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 16px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const SharedFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-top: 4px;

  input[type="email"] {
    min-height: 42px;
    font-size: 14px;
  }
`;

const LargeTextareaWrap = styled.div`
  textarea {
    min-height: 120px;
    resize: vertical;
    font-size: 14px;
    line-height: 1.45;
  }
`;

const SectionTitle = styled.p`
  margin: 8px 0 10px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #263b4b;
`;

const TableShell = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: auto;
  max-height: 320px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: #263b4b;

  thead {
    background: #f8fafc;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid #eef2f7;
    text-align: left;
    vertical-align: middle;
  }

  th {
    font-size: 11px;
    letter-spacing: 0.4px;
    color: #475569;
  }

  tbody tr:hover {
    background: #f8fafc;
  }
`;

const DeleteButton = styled.button`
  all: unset;
  cursor: pointer;
  color: #b91c1c;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  padding: 6px 10px;
  border-radius: 6px;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(185, 28, 28, 0.08);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmptyRow = styled.div`
  padding: 24px 12px;
  text-align: center;
  font-size: 13px;
  color: #64748b;
`;

const AddLicencasButton = styled.button`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  width: 100%;
  margin-top: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px dashed #94a3b8;
  background: #f8fafc;
  color: #1d4ed8;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-align: center;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: #eff6ff;
    border-color: #2563eb;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const formatDataBR = (value) => {
  if (value == null || value === "") return "—";
  const str = String(value).trim();
  if (!str) return "—";

  const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
  if (isoMatch) {
    const [, y, m, d, hh, mm] = isoMatch;
    if (hh != null && mm != null) return `${d}/${m}/${y} ${hh}:${mm}`;
    return `${d}/${m}/${y}`;
  }

  return str;
};

const dash = (value) => {
  if (value == null || String(value).trim() === "") return "—";
  return String(value).trim();
};

const formatLicencaLabel = (item) =>
  `${dash(item?.fornecedor)} - ${dash(item?.tipo)}`;

const normalizeObservacoes = (value) => {
  const str = value == null ? "" : String(value).trim();
  return str;
};

export default function ModalDetalhesLicencasColaborador({ colaborador, onClose, onChanged }) {
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [licencas, setLicencas] = useState([]);
  const [email, setEmail] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [initialEmail, setInitialEmail] = useState("");
  const [initialObservacoes, setInitialObservacoes] = useState("");
  const [addLicencasOpen, setAddLicencasOpen] = useState(false);

  const matricula =
    colaborador?.matricula != null && colaborador.matricula !== "—"
      ? String(colaborador.matricula).trim()
      : "";

  const applyContatoFromLicencas = useCallback((list) => {
    const first = Array.isArray(list) && list.length > 0 ? list[0] : null;
    const nextEmail = first?.email != null ? String(first.email).trim() : "";
    const nextObservacoes = normalizeObservacoes(first?.observacoes);
    setEmail(nextEmail);
    setObservacoes(nextObservacoes);
    setInitialEmail(nextEmail);
    setInitialObservacoes(nextObservacoes);
  }, []);

  const requestLicencas = useCallback(async () => {
    if (!matricula) {
      setLicencas([]);
      applyContatoFromLicencas([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data } = await apiRef.current.get(
        `${LICENCAS_ENDPOINT}?matricula=${matricula}`
      );
      const list = Array.isArray(data) ? data : data?.data ?? [];
      const parsed = Array.isArray(list) ? list : [];
      setLicencas(parsed);
      applyContatoFromLicencas(parsed);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Erro ao carregar licenças do colaborador.");
    } finally {
      setLoading(false);
    }
  }, [applyContatoFromLicencas, matricula]);

  useEffect(() => {
    requestLicencas();
  }, [requestLicencas]);

  const emailValido = useMemo(() => {
    const e = String(email || "").trim();
    if (!e) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  }, [email]);

  const hasChanges = useMemo(() => {
    return (
      String(email || "").trim() !== String(initialEmail || "").trim() ||
      normalizeObservacoes(observacoes) !== normalizeObservacoes(initialObservacoes)
    );
  }, [email, initialEmail, initialObservacoes, observacoes]);

  const canSave =
    licencas.length > 0 &&
    hasChanges &&
    emailValido &&
    !saving &&
    !loading;

  const totalAtivas = licencas.length;

  const idsTiposAtivos = useMemo(
    () => licencas.map((l) => l.id_tipo_licenca).filter((id) => id != null),
    [licencas]
  );

  const handleLicencasAdicionadas = async () => {
    setAddLicencasOpen(false);
    await requestLicencas();
    onChanged?.();
  };

  const handleSalvarAlteracoes = async (e) => {
    e.preventDefault();
    if (!canSave) return;

    try {
      setSaving(true);
      await apiRef.current.post(UPDATE_CONTATO_ENDPOINT, {
        matricula,
        email: email.trim(),
        observacoes: normalizeObservacoes(observacoes) || null,
      });
      await requestLicencas();
      onChanged?.();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Erro ao salvar alterações.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemover = async (licenca) => {
    const label = formatLicencaLabel(licenca);
    const ok = window.confirm(`Encerrar a licença "${label}"?`);
    if (!ok) return;

    try {
      setRemovingId(licenca.id);
      await apiRef.current.post(REMOVE_LICENCA_ENDPOINT, { id: licenca.id });
      await requestLicencas();
      onChanged?.();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Erro ao encerrar licença.");
    } finally {
      setRemovingId(null);
    }
  };

  const tituloColaborador = useMemo(() => {
    const nome = dash(colaborador?.nome);
    return nome !== "—" ? nome : `Matrícula ${matricula}`;
  }, [colaborador?.nome, matricula]);

  if (!colaborador || !matricula) return null;

  return (
    <Overlay state={true}>
      <Modal header close={onClose} size="lg">
        <Formulario onSubmit={handleSalvarAlteracoes}>
          <SectionTitle>LICENÇAS ATIVAS — {tituloColaborador.toUpperCase()}</SectionTitle>

          <InfoGrid>
            <FormComp title="MATRÍCULA" type="text" readOnly valor={matricula} />
            <FormComp title="NOME" type="text" readOnly valor={dash(colaborador.nome)} />
            <FormComp title="FUNÇÃO" type="text" readOnly valor={dash(colaborador.funcao)} />
            <FormComp
              title="CENTRO DE CUSTO"
              type="text"
              readOnly
              valor={dash(colaborador.centro_custo)}
            />
          </InfoGrid>

          {!loading && (
            <>
              <SectionTitle>DADOS COMPARTILHADOS DAS LICENÇAS</SectionTitle>
              <SharedFields>
                <FormComp
                  title="E-MAIL DO COLABORADOR"
                  type="email"
                  placeholder="EX: colaborador@palmont.com.br"
                  valor={email}
                  name="email"
                  changeFunc={(e) => setEmail(e.target.value)}
                  required
                  invalid={email.length > 0 && !emailValido}
                />
                <LargeTextareaWrap>
                  <FormComp
                    title="OBSERVAÇÕES"
                    type="textarea"
                    placeholder="INFORME OBSERVAÇÕES APLICÁVEIS A TODAS AS LICENÇAS ATIVAS"
                    valor={observacoes}
                    name="observacoes"
                    changeFunc={(e) => setObservacoes(e.target.value)}
                  />
                </LargeTextareaWrap>
              </SharedFields>
              {licencas.length > 0 && (
                <FormComp
                  type="submit"
                  valor={saving ? "SALVANDO..." : "SALVAR ALTERAÇÕES"}
                  disabled={!canSave}
                />
              )}
            </>
          )}

          {hasChanges && licencas.length > 0 && !canSave && email.length > 0 && !emailValido && (
            <p style={{ color: "#B91C1C", fontSize: "12px", marginTop: "-6px" }}>
              Informe um e-mail válido para salvar ou adicionar licenças.
            </p>
          )}

          <SectionTitle>
            LICENÇAS ({loading ? "..." : totalAtivas} ativa{totalAtivas === 1 ? "" : "s"})
          </SectionTitle>

          {loading ? (
            <EmptyRow>Carregando licenças...</EmptyRow>
          ) : licencas.length === 0 ? (
            <EmptyRow>Nenhuma licença ativa para este colaborador.</EmptyRow>
          ) : (
            <TableShell>
              <Table>
                <thead>
                  <tr>
                    <th>LICENÇA</th>
                    <th>DATA INÍCIO</th>
                    <th style={{ width: "90px", textAlign: "center" }}>AÇÃO</th>
                  </tr>
                </thead>
                <tbody>
                  {licencas.map((licenca) => (
                    <tr key={licenca.id}>
                      <td>{formatLicencaLabel(licenca)}</td>
                      <td>{formatDataBR(licenca.data_inicio)}</td>
                      <td style={{ textAlign: "center" }}>
                        <DeleteButton
                          type="button"
                          disabled={removingId === licenca.id || saving}
                          onClick={() => handleRemover(licenca)}
                        >
                          {removingId === licenca.id ? "..." : "APAGAR"}
                        </DeleteButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableShell>
          )}

          {!loading && (
            <AddLicencasButton
              type="button"
              disabled={!emailValido || saving}
              onClick={() => setAddLicencasOpen(true)}
            >
              + ADICIONAR LICENÇAS
            </AddLicencasButton>
          )}
        </Formulario>
      </Modal>

      {addLicencasOpen && (
        <ModalAdicionarLicencasColaborador
          matricula={matricula}
          email={email}
          observacoes={observacoes}
          idsTiposAtivos={idsTiposAtivos}
          onClose={() => setAddLicencasOpen(false)}
          onSaved={handleLicencasAdicionadas}
        />
      )}
    </Overlay>
  );
}
